/* eslint react/prop-types: 0 */

import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import classnames from 'classnames';

// components
import BuyerPreQuotedOptions from './BuyerPreQuotedOptions';
import BuyerNeedsDeliveryQuote from './BuyerNeedsDeliveryQuote';
import BuyerLocalPickup from './BuyerLocalPickup';
import BuyerArrangeDelivery from './BuyerArrangeDelivery';
import BuyerCustomQuote from './BuyerCustomQuote';
import BuyerShipmentRequest from './BuyerShipmentRequest';
import CheckoutAllowCustomQuote from './CheckoutAllowCustomQuote';
import DeactivatedQuotes from './DeactivatedQuotes';
import CheckoutShippingDetails from './CheckoutShippingDetails';
import { filterQuotes } from '../helpers/filterQuotes';

import style from './styles/checkoutDeliveryOptions.scss';

import get from 'lodash.get';

function CheckoutDeliveryOptions({ onSelect, ...props }) {
    const {
        item,
        transaction,
        buyer,
        deliveryOptions = [],
        preCheckoutTransaction,
        shipmentType,
        shipmentQuoteId,
        isComputing,
        quantity,
        viewer,
        preloadedDuties,
        className,
    } = props;
    const preQuotes = filterQuotes(deliveryOptions);
    const localPickupQuote = deliveryOptions.find(q => q.isLocalPickup);
    const showLeadTime = !get(transaction, 'sku.isAvailableNow');

    return (
        <CheckoutAllowCustomQuote {...{ buyer, deliveryOptions }}>
            {({ allowCustomQuote }) => (
                <form className={classnames(style.wrapper, className)}>
                    {preQuotes.length ? (
                        <BuyerPreQuotedOptions
                            {...{
                                item,
                                deliveryOptions: preQuotes,
                                shipmentType,
                                shipmentQuoteId,
                                onSelect,
                                transaction,
                                isComputing,
                                viewer,
                                preloadedDuties,
                            }}
                        />
                    ) : (
                        <BuyerNeedsDeliveryQuote
                            shipmentType={shipmentType}
                            shipmentQuoteId={shipmentQuoteId}
                            onSelect={onSelect}
                            isComputing={isComputing}
                        />
                    )}
                    <DeactivatedQuotes
                        deliveryOptions={preQuotes}
                        preCheckoutTransaction={preCheckoutTransaction}
                        sku={transaction?.sku || null}
                        quantity={quantity}
                    />
                    {allowCustomQuote && (
                        <BuyerCustomQuote
                            {...{ shipmentType, shipmentQuoteId, onSelect, isComputing }}
                        />
                    )}
                    {localPickupQuote && (
                        <BuyerLocalPickup
                            {...{
                                item,
                                shipmentType,
                                shipmentQuoteId,
                                localPickupQuote,
                                onSelect,
                                isComputing,
                            }}
                        />
                    )}
                    <BuyerArrangeDelivery {...{ item, shipmentType, onSelect, isComputing }} />
                    <CheckoutShippingDetails
                        {...{ transaction: transaction || null, showLeadTime, item }}
                    />

                    <BuyerShipmentRequest {...{ buyer, deliveryOptions, allowCustomQuote }} />
                </form>
            )}
        </CheckoutAllowCustomQuote>
    );
}

CheckoutDeliveryOptions.propTypes = {
    item: PropTypes.object.isRequired,
    transaction: PropTypes.object,
    buyer: PropTypes.any,
    viewer: PropTypes.object,
    deliveryOptions: PropTypes.array.isRequired,
    preloadedDuties: PropTypes.object.isRequired,
    preCheckoutTransaction: PropTypes.object,
    shipmentType: PropTypes.string.isRequired,
    shipmentQuoteId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    isComputing: PropTypes.bool,
    quantity: PropTypes.number,
};

export default createFragmentContainer(CheckoutDeliveryOptions, {
    item: graphql`
        fragment CheckoutDeliveryOptions_item on Item {
            ...BuyerLocalPickup_item
            ...BuyerArrangeDelivery_item
            ...CheckoutShippingDetails_item
        }
    `,
    buyer: graphql`
        fragment CheckoutDeliveryOptions_buyer on User {
            ...BuyerShipmentRequest_buyer
            ...CheckoutAllowCustomQuote_buyer
        }
    `,
    deliveryOptions: graphql`
        fragment CheckoutDeliveryOptions_deliveryOptions on ShipmentQuote @relay(plural: true) {
            ...BuyerLocalPickup_localPickupQuote
            ...BuyerPreQuotedOptions_deliveryOptions
            ...BuyerShipmentRequest_deliveryOptions
            ...CheckoutAllowCustomQuote_deliveryOptions
            ...DeactivatedQuotes_deliveryOptions
            serviceId
            isLocalPickup
            showQuoteAsPending
        }
    `,
    transaction: graphql`
        fragment CheckoutDeliveryOptions_transaction on Transaction {
            serviceId
            ...BuyerPreQuotedOptions_transaction
            ...CheckoutShippingDetails_transaction
            sku {
                ...DeactivatedQuotes_sku
                isAvailableNow
            }
        }
    `,
    preCheckoutTransaction: graphql`
        fragment CheckoutDeliveryOptions_preCheckoutTransaction on Transaction {
            ...DeactivatedQuotes_preCheckoutTransaction
        }
    `,
});
